import React, { useState } from 'react';
import { db } from '../config/firebase';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import toast from 'react-hot-toast';

const CleanupFirestore = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const addResult = (message, type = 'info') => {
    setResults(prev => [...prev, { message, type, timestamp: new Date().toLocaleTimeString() }]);
  };

  const cleanupFirestore = async () => {
    setLoading(true);
    setResults([]);
    
    try {
      addResult('🧹 Starting Firestore cleanup...', 'info');

      // Collections to clean up
      const collectionsToClean = [
        'clinics',
        'tenants', 
        'users'
      ];

      for (const collectionName of collectionsToClean) {
        try {
          addResult(`🔍 Checking ${collectionName} collection...`, 'info');
          
          const querySnapshot = await getDocs(collection(db, collectionName));
          const docCount = querySnapshot.size;
          
          if (docCount === 0) {
            addResult(`✅ ${collectionName}: No documents to clean`, 'success');
            continue;
          }

          addResult(`📄 Found ${docCount} documents in ${collectionName}`, 'info');
          
          // Delete each document
          const deletePromises = querySnapshot.docs.map(document => 
            deleteDoc(doc(db, collectionName, document.id))
          );
          
          await Promise.all(deletePromises);
          addResult(`✅ Deleted ${docCount} documents from ${collectionName}`, 'success');
          
        } catch (error) {
          addResult(`❌ Error cleaning ${collectionName}: ${error.message}`, 'error');
        }
      }

      addResult('🎉 Firestore cleanup completed!', 'success');
      toast.success('Firestore cleanup completed successfully!');
      
    } catch (error) {
      addResult(`❌ Cleanup failed: ${error.message}`, 'error');
      toast.error('Cleanup failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            🧹 Firestore Cleanup
          </h1>
          
          <div className="mb-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <h3 className="font-medium text-yellow-900 mb-2">⚠️ Warning</h3>
              <p className="text-sm text-yellow-700">
                This will delete ALL documents from the following collections:
              </p>
              <ul className="text-sm text-yellow-700 mt-2 ml-4">
                <li>• clinics (all clinic data)</li>
                <li>• tenants (all tenant data)</li>
                <li>• users (all user profiles)</li>
              </ul>
              <p className="text-sm text-yellow-700 mt-2">
                <strong>This action cannot be undone!</strong> Only use this in development.
              </p>
            </div>
            
            <p className="text-gray-600 mb-4">
              This cleanup will resolve "slug taken" errors by removing all existing clinic and tenant documents.
              After cleanup, you'll need to run the dev setup again to recreate the basic structure.
            </p>
          </div>

          <button
            onClick={cleanupFirestore}
            disabled={loading}
            className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${
              loading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            {loading ? '🧹 Cleaning up...' : '🗑️ Clean Up Firestore'}
          </button>

          {results.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Cleanup Progress:</h3>
              <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                {results.map((result, index) => (
                  <div
                    key={index}
                    className={`flex items-start space-x-3 py-2 ${
                      result.type === 'error' ? 'text-red-700' :
                      result.type === 'success' ? 'text-green-700' :
                      'text-gray-700'
                    }`}
                  >
                    <span className="text-xs text-gray-500 mt-1 min-w-[60px]">
                      {result.timestamp}
                    </span>
                    <span className="flex-1">{result.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">After Cleanup:</h3>
            <ol className="text-sm text-blue-700 space-y-1">
              <li>1. Navigate to <code>/dev-setup</code></li>
              <li>2. Click "Setup Dev Environment" to recreate basic structure</li>
              <li>3. Try creating a clinic again - the slug error should be resolved</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CleanupFirestore;
